import { useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameState } from '@/types';
import Card from './Card';
import PlayerSeat from './PlayerSeat';
import PotDisplay from './PotDisplay';
import ActionPanel from './ActionPanel';
import { useGameStore } from '@/store/game-store';

interface PokerTableProps {
  gameState: GameState;
}

// 计算座位位置（椭圆形布局）- 座位在桌子外面
function calculateSeatPositions(count: number): { x: number; y: number }[] {
  const positions: { x: number; y: number }[] = [];
  const radiusX = 58; // 水平半径（百分比）- 扩大到桌子外面
  const radiusY = 52; // 垂直半径（百分比）- 扩大到桌子外面
  const centerX = 50;
  const centerY = 48;

  // 从庄家位置（底部）开始
  const startAngle = Math.PI / 2;

  for (let i = 0; i < count; i++) {
    const angle = startAngle + (2 * Math.PI * i) / count;
    const x = centerX + radiusX * Math.cos(angle);
    let y = centerY + radiusY * Math.sin(angle);
    
    // 底部玩家（庄家位置）向上移动，避免被操作面板遮挡
    if (i === 0) {
      y = Math.min(y, 88); // 限制底部玩家的最大Y位置
    }
    
    positions.push({ x, y });
  }

  return positions;
}

export default function PokerTable({ gameState }: PokerTableProps) {
  const { performAction, getValidActions, timeLeft, maxTime, engine } = useGameStore();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPlayerIndexRef = useRef(gameState.currentPlayerIndex);
  
  const seatPositions = useMemo(() => 
    calculateSeatPositions(gameState.players.length),
    [gameState.players.length]
  );

  const validActions = getValidActions();
  const isHumanTurn = gameState.players[gameState.currentPlayerIndex]?.isHuman;
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];

  // 倒计时逻辑 - 所有玩家都有倒计时
  useEffect(() => {
    // 游戏进行中且不是摊牌/结算阶段时启动倒计时
    if (gameState.phase !== 'showdown' && gameState.phase !== 'settlement' && gameState.phase !== 'waiting') {
      timerRef.current = setInterval(() => {
        const currentState = useGameStore.getState();
        if (currentState.timeLeft > 0) {
          useGameStore.setState({ timeLeft: currentState.timeLeft - 0.1 });
        }
      }, 100);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState.phase, gameState.currentPlayerIndex]);

  // 当玩家切换时重置计时器
  useEffect(() => {
    if (gameState.currentPlayerIndex !== prevPlayerIndexRef.current) {
      useGameStore.setState({ timeLeft: maxTime });
      prevPlayerIndexRef.current = gameState.currentPlayerIndex;
    }
  }, [gameState.currentPlayerIndex, maxTime]);

  // 超时自动弃牌
  useEffect(() => {
    if (timeLeft <= 0 && currentPlayer && engine && gameState.phase !== 'showdown' && gameState.phase !== 'settlement') {
      // 超时自动弃牌
      const validActions = engine.getValidActions(currentPlayer.id);
      if (validActions.includes('fold')) {
        const newState = engine.handleAction(currentPlayer.id, { type: 'fold' });
        useGameStore.setState({ gameState: newState, timeLeft: maxTime });
        
        // 如果是人类玩家，继续AI回合
        if (currentPlayer.isHuman && newState.phase !== 'settlement') {
          setTimeout(() => {
            const { processAITurn } = useGameStore.getState();
            processAITurn();
          }, 500);
        }
      } else if (validActions.includes('check')) {
        const newState = engine.handleAction(currentPlayer.id, { type: 'check' });
        useGameStore.setState({ gameState: newState, timeLeft: maxTime });
        
        if (currentPlayer.isHuman && newState.phase !== 'settlement') {
          setTimeout(() => {
            const { processAITurn } = useGameStore.getState();
            processAITurn();
          }, 500);
        }
      }
    }
  }, [timeLeft, currentPlayer, engine, gameState.phase, maxTime]);

  return (
    <div className="relative w-full h-full min-h-[600px] flex items-center justify-center p-4">
      {/* 牌桌背景 */}
      <div className="relative w-full max-w-4xl aspect-[4/3]">
        {/* 桌布 */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-green-700 to-green-900 rounded-[40%] border-8 border-amber-900 shadow-2xl"
          style={{
            boxShadow: 'inset 0 0 100px rgba(0,0,0,0.5), 0 20px 60px rgba(0,0,0,0.5)',
          }}
        >
          {/* 内部装饰线 */}
          <div className="absolute inset-4 border-2 border-white/10 rounded-[38%]" />
          <div className="absolute inset-8 border border-white/5 rounded-[36%]" />
        </div>

        {/* 中央区域 - 公共牌和底池 */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-6">
          {/* 公共牌 */}
          <div className="flex gap-2">
            <AnimatePresence mode="popLayout">
              {gameState.communityCards.map((card, i) => (
                <motion.div
                  key={card.id}
                  initial={{ rotateY: 180, scale: 0.5, opacity: 0 }}
                  animate={{ rotateY: 0, scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ 
                    delay: i * 0.15, 
                    type: 'spring', 
                    stiffness: 200,
                    damping: 15 
                  }}
                >
                  <Card card={card} size="lg" animate={false} />
                </motion.div>
              ))}
              
              {/* 占位符 */}
              {Array.from({ length: 5 - gameState.communityCards.length }).map((_, i) => (
                <div 
                  key={`placeholder-${i}`}
                  className="w-20 h-28 rounded-lg bg-black/20 border-2 border-white/5"
                />
              ))}
            </AnimatePresence>
          </div>

          {/* 底池显示 */}
          <PotDisplay pots={gameState.pots} />

          {/* 当前阶段 */}
          <div className="absolute -bottom-20">
            <span className="bg-black/60 text-white/80 px-4 py-1 rounded-full text-sm uppercase tracking-wider">
              {getPhaseName(gameState.phase)}
            </span>
          </div>
        </div>

        {/* 玩家座位 */}
        {gameState.players.map((player, i) => (
          <PlayerSeat
            key={player.id}
            player={player}
            position={seatPositions[i]}
            isActive={gameState.currentPlayerIndex === i}
            isDealer={gameState.dealerIndex === i}
            gamePhase={gameState.phase}
            showCards={gameState.phase === 'showdown' || gameState.phase === 'settlement'}
            timeLeft={gameState.currentPlayerIndex === i ? timeLeft : undefined}
            maxTime={maxTime}
          />
        ))}
      </div>

      {/* 操作面板 */}
      {isHumanTurn && gameState.phase !== 'showdown' && gameState.phase !== 'settlement' && (
        <ActionPanel
          state={gameState}
          validActions={validActions}
          onAction={performAction}
        />
      )}

      {/* 游戏信息栏 */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2">
          <div className="text-white/60 text-xs uppercase tracking-wider">盲注</div>
          <div className="text-white font-mono">
            {gameState.blinds.small}/{gameState.blinds.big}
          </div>
        </div>

        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2">
          <div className="text-white/60 text-xs uppercase tracking-wider">手牌</div>
          <div className="text-white font-mono">#{gameState.handNumber}</div>
        </div>
      </div>
    </div>
  );
}

function getPhaseName(phase: string): string {
  const names: Record<string, string> = {
    'waiting': '等待开始',
    'preflop': '翻牌前',
    'flop': '翻牌圈',
    'turn': '转牌圈',
    'river': '河牌圈',
    'showdown': '摊牌',
    'settlement': '结算',
  };
  return names[phase] || phase;
}
